import { useCallback, useMemo, useState } from 'react'

import {
  READY_BELL,
  READY_BELL_STORAGE_KEY,
  readyBellEnabled,
  storedReadyBellValue,
} from '../arrival-chime'
import { useChimeOutput } from '../chime-player'

/**
 * The kitchen's *Hotová*, rung on Otvorené stoly - FRONTEND.md §9.675, owner O56, a missed port.
 *
 * <p>The owner: *"zvoncek z kuchyne na restiku ... aby to zazvonilo v otvorených stoloch aby vedeli upozorniť
 * čašníka že majú obj hotovú"*. Legacy's `restaurant.tsx` had exactly this - `playReadySound` - with its own mute,
 * and the port carried the screen without it.
 *
 * <p><b>Its own switch, on unless switched off</b> (`readyBellEnabled`), stored per device under its own key, so
 * the new-order chime's switch neither silences nor wakes it.
 *
 * <p><b>Switching it back on rings it once.</b> The waiter hears what to listen for, and the press is the gesture a
 * browser wants before it lets an `AudioContext` run - which matters for a sound that is on by default and would
 * otherwise first try to play from a poll.
 *
 * <p><b>`ring` is the screen's to call</b> when an order has turned ready; this hook does not know about orders.
 */
export function useReadyBell(): {
  enabled: boolean
  toggle: () => void
  ring: () => void
} {
  const [enabled, setEnabled] = useState(() => {
    try {
      return readyBellEnabled(localStorage.getItem(READY_BELL_STORAGE_KEY))
    } catch {
      // A browser refusing storage keeps legacy's default rather than going quiet.
      return true
    }
  })
  const output = useChimeOutput()

  const ring = useCallback(() => {
    if (enabled) output(READY_BELL)
  }, [enabled, output])

  const toggle = useCallback(() => {
    const next = !enabled
    try {
      localStorage.setItem(READY_BELL_STORAGE_KEY, storedReadyBellValue(next))
    } catch {
      // A browser refusing storage still gets the choice for this session.
    }
    setEnabled(next)
    if (next) output(READY_BELL)
  }, [enabled, output])

  return useMemo(() => ({ enabled, toggle, ring }), [enabled, toggle, ring])
}
